/**
 * Timezone utilities for client-side timezone handling
 */

/**
 * Get user's timezone using JavaScript
 * Returns IANA timezone identifier (e.g., "America/New_York", "Europe/Moscow")
 */
export const getUserTimezone = (): string => {
  try {
    // Modern browsers support Intl API
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // Fallback: calculate from offset
    const offset = -new Date().getTimezoneOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? '+' : '-';
    return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
};

/**
 * Get timezone offset in minutes
 */
export const getTimezoneOffset = (): number => {
  return -new Date().getTimezoneOffset();
};

/**
 * Convert browser timezone to GMT format (e.g., "GMT+3", "GMT-5")
 * Used for storing client timezone in database
 */
export const getUserTimezoneGMT = (): string => {
  try {
    const offset = getTimezoneOffset();
    const hours = Math.floor(offset / 60);
    
    if (hours === 0) {
      return 'GMT';
    } else if (hours > 0) {
      return `GMT+${hours}`;
    } else {
      return `GMT${hours}`; // Negative sign is already included
    }
  } catch (error) {
    return 'GMT'; // Fallback to GMT
  }
};

/**
 * Format timezone for display (auto-detects user's timezone)
 * Examples: "EST (UTC-5)", "MSK (UTC+3)"
 */
export const formatUserTimezone = (): string => {
  return formatTimezone(getUserTimezone());
};

/**
 * Format timezone for display
 * Examples: "America/New_York" -> "EST (UTC-5)", "Europe/Moscow" -> "MSK (UTC+3)"
 */
export const formatTimezone = (timezone: string): string => {
  try {
    // Get offset in minutes
    const offset = getTimezoneOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? '+' : '-';
    const offsetStr = `UTC${sign}${hours.toString().padStart(2, '0')}${minutes > 0 ? ':' + minutes.toString().padStart(2, '0') : ''}`;
    
    // Try to get timezone abbreviation
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      
      const parts = formatter.formatToParts(now);
      const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || '';
      
      if (timeZoneName && timeZoneName !== timezone) {
        return `${timeZoneName} (${offsetStr})`;
      }
    } catch (e) {
      // Fall through to return just offset
    }
    
    // If we can't get abbreviation, return timezone name and offset
    const shortName = timezone.includes('/') ? timezone.split('/').pop()?.replace('_', ' ') : timezone;
    return `${shortName} (${offsetStr})`;
  } catch (error) {
    // Fallback: just return offset
    const offset = getTimezoneOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? '+' : '-';
    return `UTC${sign}${hours.toString().padStart(2, '0')}${minutes > 0 ? ':' + minutes.toString().padStart(2, '0') : ''}`;
  }
};

/**
 * Convert UTC date string to user's local timezone
 * @param utcDateString - Date string in UTC (ISO format or any parseable format)
 * @returns Date object in user's local timezone
 */
export const utcToLocal = (utcDateString: string | Date): Date => {
  if (utcDateString instanceof Date) {
    return utcDateString;
  }
  
  // Parse the date string
  // If it contains 'Z', it's already in UTC format
  // If it contains '+' or '-' after position 10, it has timezone offset
  // Otherwise, assume it's UTC
  let date: Date;
  
  if (utcDateString.includes('Z')) {
    // ISO format with Z suffix (UTC)
    date = new Date(utcDateString);
  } else if (utcDateString.includes('+') || (utcDateString.includes('-') && utcDateString.length > 19)) {
    // ISO format with timezone offset (e.g., "2025-11-23T06:00:00+03:00")
    date = new Date(utcDateString);
  } else {
    // No timezone info - assume UTC and create date explicitly in UTC
    // Format: "2025-11-23T06:00:00" or "2025-11-23 06:00:00"
    const normalized = utcDateString.replace(' ', 'T');
    if (!normalized.includes('T')) {
      // Just a date, add time
      date = new Date(`${normalized}T00:00:00Z`);
    } else {
      // Has time but no timezone - treat as UTC
      date = new Date(`${normalized}Z`);
  }
  }
  
  // Date object automatically represents time in local timezone when accessed
  // So we just return it - Intl.DateTimeFormat will use local timezone
  return date;
};

/**
 * Format date/time in user's timezone
 * @param utcDateString - Date string in UTC
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatLocalTime = (
  utcDateString: string | Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }
): string => {
  const localDate = utcToLocal(utcDateString);
  return new Intl.DateTimeFormat('en-US', options).format(localDate);
};

/**
 * Format time only in user's timezone
 * @param utcDateString - Date string in UTC
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted time string (e.g., "2:30 PM")
 */
export const formatLocalTimeOnly = (
  utcDateString: string | Date,
  includeSeconds: boolean = false
): string => {
  // Ensure we're working with a proper Date object
  let date: Date;
  if (utcDateString instanceof Date) {
    date = utcDateString;
  } else {
    // Parse the date string
    // If it contains 'Z', it's UTC
    // If it contains '+' or '-' after position 10, it has timezone offset
    // Otherwise, assume it's UTC and append 'Z'
    if (utcDateString.includes('Z')) {
      date = new Date(utcDateString);
    } else if (utcDateString.includes('+') || (utcDateString.includes('-') && utcDateString.length > 19)) {
      // Has timezone offset
      date = new Date(utcDateString);
    } else {
      // No timezone - assume UTC
      const normalized = utcDateString.replace(' ', 'T');
      date = normalized.includes('T') ? new Date(`${normalized}Z`) : new Date(`${normalized}T00:00:00Z`);
    }
  }
  
  // Use Intl.DateTimeFormat which automatically handles timezone conversion
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined,
    hour12: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }).format(date);
};

/**
 * Format date only in user's timezone
 * @param utcDateString - Date string in UTC
 * @returns Formatted date string (e.g., "Nov 20, 2025")
 */
export const formatLocalDateOnly = (utcDateString: string | Date): string => {
  return formatLocalTime(utcDateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date and time in user's timezone
 * @param utcDateString - Date string in UTC
 * @returns Formatted date and time string (e.g., "Nov 20, 2025, 2:30 PM")
 */
export const formatLocalDateTime = (utcDateString: string | Date): string => {
  return formatLocalTime(utcDateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Convert local time (HH:MM) to UTC time (HH:MM)
 * Used for working hours - when user selects time in their timezone, convert to UTC for storage
 * @param localTime - Time string in format "HH:MM" (user's local timezone)
 * @param date - Optional date to use for conversion (defaults to today)
 * @returns Time string in format "HH:MM" (UTC)
 */
export const localTimeToUTC = (localTime: string, date: Date = new Date()): string => {
  const [hours, minutes] = localTime.split(':').map(Number);
  
  // Create a date object for today at the specified local time
  const localDate = new Date(date);
  localDate.setHours(hours, minutes, 0, 0);
  
  // Get UTC hours and minutes
  const utcHours = localDate.getUTCHours();
  const utcMinutes = localDate.getUTCMinutes();
  
  return `${utcHours.toString().padStart(2, '0')}:${utcMinutes.toString().padStart(2, '0')}`;
};

/**
 * Convert UTC time (HH:MM) to local time (HH:MM)
 * Used for working hours - when displaying UTC time from DB, convert to user's timezone
 * @param utcTime - Time string in format "HH:MM" (UTC)
 * @param date - Optional date to use for conversion (defaults to today)
 * @returns Time string in format "HH:MM" (user's local timezone)
 */
export const utcTimeToLocal = (utcTime: string, date: Date = new Date()): string => {
  const [hours, minutes] = utcTime.split(':').map(Number);
  
  // Create a date object for today at the specified UTC time
  const utcDate = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0
  ));
  
  // Get local hours and minutes
  const localHours = utcDate.getHours();
  const localMinutes = utcDate.getMinutes();
  
  return `${localHours.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')}`;
};

